import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod/v4";
import { ApprovalActions } from "@/components/approvals/approval-actions";
import { UploadForm } from "@/components/driver/upload-form";
import { Button } from "@/components/ui/button";
import { useSlotById } from "@/hooks/use-driver-board";
import { useProfile } from "@/hooks/use-profile";
import type { UserRole } from "@/lib/roles";
import { canApproveSetups, canUploadSetups } from "@/lib/roles";

const searchSchema = z.object({
  week: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_auth/app/dashboard/upload/$slotId")({
  validateSearch: searchSchema,
  component: UploadPage,
});

function UploadPage() {
  const { slotId } = Route.useParams();
  const { week } = Route.useSearch();
  const navigate = useNavigate();

  const goBack = () => navigate({ to: "/app/dashboard", search: week ? { week } : {} });
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: slot, isLoading: slotLoading } = useSlotById(slotId);

  const profileReady = !profileLoading && profile !== undefined;
  const role = (profile?.role ?? "user") as UserRole;
  const hasAccess = canUploadSetups(role);

  useEffect(() => {
    if (profileReady && !hasAccess) {
      navigate({ to: "/app/setups", search: {} as any });
    }
  }, [profileReady, hasAccess, navigate]);

  if (!profileReady || slotLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (!slot) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
          Back to Task Board
        </Button>
        <div className="rounded-xl border border-dashed border-border/50 py-16 text-center">
          <p className="text-muted-foreground">Setup slot not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={goBack}>
        <ArrowLeft className="h-4 w-4" />
        Back to Task Board
      </Button>

      {/* Title */}
      <h1 className="font-[Bebas_Neue] text-3xl text-foreground">Upload Setup</h1>

      {/* Approve/reject for admins viewing a pending setup */}
      {slot.existing_setup?.status === "pending_review" && canApproveSetups(role) && profile && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <p className="text-sm font-medium text-amber-300">Pending review</p>
          <p className="mt-0.5 mb-3 text-xs text-amber-300/80">
            Approve or reject this setup without leaving the page.
          </p>
          <ApprovalActions setupId={slot.existing_setup.id} approverId={profile.id} />
        </div>
      )}

      {/* Upload form */}
      <UploadForm slot={slot} onSuccess={goBack} />
    </div>
  );
}
